import { findNode, parseTocInternal } from "../content/content";
import { MENU_ITEM_LOCATION } from "../menu";
import { diveToChapterWithContent } from "../reader/reader-chapters-navigator";
import { getFolderType } from "../search/search";
import { BreadcrumbType } from "./constants";
import { DEFAULT_LANGUAGE } from "../multilang/constants";

export const clearUnnecessaryPropertiesForBreadcrumbs = (breadcrumbs) =>
  breadcrumbs.map(
    ({
      className = null,
      id,
      label,
      children,
      url,
      title,
      book_id = null,
      folder_id = null,
      lang = null,
    }) => {
      const elem = {
        id,
        label: label || title || "",
        url: url ? url.replace("/text", "") : null,
        book_id,
        folder_id,
        lang,
        className,
      };

      if (children) {
        elem.children = children.map((child) => ({
          ...child,
          children: [],
        }));
      }

      return elem;
    }
  );

export const makeBreadcrumbChapter = (chapter, children) => {
  return {
    type: BreadcrumbType.chapter,
    id: chapter.id,
    label: chapter.title,
    children: children && children.length ? children.map(makeBreadcrumbChapter) : null,
  };
};

const makeBreadcrumbBook = (book, children) => {
  let bookChildren = null;

  const tocTree = children && children.length ? parseTocInternal(children, 0) : null;
  if (tocTree && tocTree.length) {
    bookChildren = tocTree[0].children;
  }

  return {
    type: BreadcrumbType.book,
    id: book.id,
    label: book.title,
    children: bookChildren && bookChildren.length ? bookChildren.map(makeBreadcrumbChapter) : null,
  };
};

const makeBreadcrumbFolder = (folder, children) => {
  const out = {
    type: BreadcrumbType.folder,
    id: folder.id,
    label: folder.label || folder.title || "",
    folder_id: folder.folder_id || "",
    lang: folder.lang || "",
    className: folder.className || "",
  };

  if (children && children.length) {
    const isChildrenBooks = !!children[0].book_id;
    out.children = isChildrenBooks
      ? children.map(makeBreadcrumbBook)
      : children.map(makeBreadcrumbFolder);
  }

  return out;
};

const makeBreadcrumbLanguage = (language, children) => {
  let childrenBreadcrumbs = null;

  if (children && children.length) {
    const isChildrenLanguages = !!children[0].code;
    childrenBreadcrumbs = isChildrenLanguages
      ? children.map(makeBreadcrumbLanguage)
      : children.map(makeBreadcrumbFolder);
  }

  return {
    type: BreadcrumbType.folder,
    id: language.id,
    label: language.label,
    children: childrenBreadcrumbs,
  };
};

export const makeBreadcrumbLandingLang = (book, langs) => {
  const bookLang = langs.find((lang) => lang.code === book.lang);
  return {
    type: BreadcrumbType.chapter,
    id: book.first_para,
    label: bookLang ? `${bookLang.label} - ${book.title}` : book.title,
  };
};

export const makeBreadcrumbLandingBook = (book) => ({
  type: BreadcrumbType.externalUrl,
  id: book.id,
  label: book.title,
  url: book.urlLanding,
});

const makeBreadcrumbMenuItem = (menuItem, children) => {
  return {
    ...menuItem,
    children: children ? children.map(makeBreadcrumbLanguage) : null,
  };
};

const getBreadcrumbsForFolderTreeTrace = (folderTreeTrace) => {
  return folderTreeTrace.map((f) => makeBreadcrumbFolder(f, f.children));
};

const getBreadcrumbsForLanguages = (isLanding, langs, languageActive) => {
  if (langs && langs.length) {
    const languageSuitable = langs.find((lang) => lang.id === languageActive) || langs[0];

    return makeBreadcrumbLanguage(languageSuitable, isLanding ? null : languageSuitable.children);
  }

  return null;
};

// Is exported temporary, check the usage place.
export const makeBreadcrumbsChapter = (toc, _chapterId) => {
  let traceTOC = [];
  if (toc && toc.length && _chapterId) {
    let chapterIdFinal;
    const chapterCurrentFixed = diveToChapterWithContent(_chapterId, toc);
    if (chapterCurrentFixed.index === -1) {
      chapterIdFinal = _chapterId;
    } else {
      chapterIdFinal = chapterCurrentFixed.chapter.id;
    }

    const tocTree = toc ? parseTocInternal(toc, 0) : undefined;
    const tocTreeFinal = tocTree && tocTree[0] && tocTree[0].children ? tocTree[0].children : toc;
    findNode(chapterIdFinal, tocTreeFinal, "id", traceTOC);
    traceTOC = traceTOC.map((c) => makeBreadcrumbChapter(c, c.children)).reverse();
  }

  return traceTOC;
};

/**
 * @param crumbs - crumbs itself
 * @param collection - influences the breadcrumbs color (e.g. brown for bible, blue for egw)
 */
export const makeBreadcrumbsObject = (crumbs, collection) => {
  return {
    // probably, rename properties to better names.
    title: crumbs && crumbs.length ? crumbs[crumbs.length - 1].label : "–",
    breadcrumbs: crumbs,
    folderType: collection || "",
  };
};

export const makeBreadcrumbsBookPage = (isLanding, langs, folderTreeTrace, book, mainMenuItem) => {
  const languageActiveFinal =
    (book && book.lang) ||
    (folderTreeTrace && folderTreeTrace.length && folderTreeTrace[0].lang) ||
    DEFAULT_LANGUAGE;
  const breadcrumbs = [
    {
      ...mainMenuItem,
      id: `/?lang=${languageActiveFinal}`,
      // children: isLanding ? null : mainMenuItem.children.filter((i) => {
      //   return !i.locations
      //     || !i.locations.length
      //     || i.locations.includes(MENU_ITEM_LOCATION.BREADCRUMBS);
      // }),
      children: isLanding ? null : langs.map(makeBreadcrumbLanguage),
    },
    // {
    //   ...MenuItems.allCollection,
    //   children: isLanding ? null : langs,
    // }
  ];
  if (languageActiveFinal) {
    const breadcrumbLanguage = getBreadcrumbsForLanguages(isLanding, langs, languageActiveFinal);
    if (breadcrumbLanguage) {
      breadcrumbs.push(breadcrumbLanguage);
    }
  }

  if (!isLanding && folderTreeTrace) {
    breadcrumbs.push(...getBreadcrumbsForFolderTreeTrace(folderTreeTrace));
  }

  if (book) {
    breadcrumbs.push(makeBreadcrumbBook(book));
  }

  return makeBreadcrumbsObject(breadcrumbs);
};

export const makeBreadcrumbsAllCollection = (
  isLanding,
  langs,
  folderTreeTrace,
  lang,
  mainMenuItem,
  menuItemAllCollection
) => {
  const languageActiveFinal =
    (folderTreeTrace && folderTreeTrace.length && folderTreeTrace[0].lang) || lang;

  const breadcrumbs = [
    {
      ...mainMenuItem,
      id: `/?lang=${languageActiveFinal}`,
      children: isLanding
        ? null
        : mainMenuItem.children.filter((i) => {
            return (
              !i.locations ||
              !i.locations.length ||
              i.locations.includes(MENU_ITEM_LOCATION.BREADCRUMBS)
            );
          }),
    },
    makeBreadcrumbMenuItem(menuItemAllCollection, isLanding ? null : langs),
  ];

  if (languageActiveFinal) {
    const breadcrumbLanguage = getBreadcrumbsForLanguages(isLanding, langs, languageActiveFinal);
    if (breadcrumbLanguage) {
      breadcrumbs.push(breadcrumbLanguage);
    }
  }

  if (folderTreeTrace) {
    breadcrumbs.push(...getBreadcrumbsForFolderTreeTrace(folderTreeTrace));
  }

  return makeBreadcrumbsObject(breadcrumbs, getFolderType(folderTreeTrace, "add_class"));
};

export const makeBreadcrumbsReader = (
  isLanding,
  langs,
  folderTreeTrace,
  book,
  toc,
  chapterId,
  mainMenuItem,
  bookTitles,
  childrenCustomSuitableLang
) => {
  const bookLang = book && book.lang;

  const breadcrumbs = [
    {
      ...mainMenuItem,
      id: `/?lang=${bookLang}`,
      children: isLanding
        ? bookTitles.map((book) => makeBreadcrumbLandingLang(book, langs))
        : langs.map(makeBreadcrumbLanguage),
    },
  ];

  if (bookLang) {
    const breadcrumbLanguage = getBreadcrumbsForLanguages(isLanding, langs, bookLang);
    if (breadcrumbLanguage) {
      if (isLanding) {
        breadcrumbLanguage.type = BreadcrumbType.nonClickable;
        if (childrenCustomSuitableLang) {
          breadcrumbLanguage.children = childrenCustomSuitableLang;
        }
      }
      breadcrumbs.push(breadcrumbLanguage);
    }
  }

  if (!isLanding && folderTreeTrace) {
    breadcrumbs.push(...getBreadcrumbsForFolderTreeTrace(folderTreeTrace));
  }

  if (book) {
    const breadcrumbBook = makeBreadcrumbBook(book, toc);
    breadcrumbs.push(
      isLanding ? { ...breadcrumbBook, type: BreadcrumbType.nonClickable } : breadcrumbBook
    );
  }

  if (toc && toc.length && chapterId) {
    breadcrumbs.push(...makeBreadcrumbsChapter(toc, chapterId));
  }

  return makeBreadcrumbsObject(breadcrumbs, book ? book.folder_color_group : "");
};

export const makeBreadcrumbsHomePage = (langs, folderTreeTrace) => {
  const breadcrumbs = [];

  const languageActiveFinal =
    (folderTreeTrace && folderTreeTrace.length && folderTreeTrace[0].lang) || DEFAULT_LANGUAGE;
  if (languageActiveFinal) {
    const breadcrumbLanguage = getBreadcrumbsForLanguages(false, langs, languageActiveFinal);
    if (breadcrumbLanguage) {
      breadcrumbs.push(breadcrumbLanguage);
    }
  }

  return makeBreadcrumbsObject(breadcrumbs);
};
